import FormattedMessage from 'I18n/components/FormattedMessage';
import { getAllActionOutputs } from 'automation-ui-action-features/action-outputs/OutputFieldsApi';
import OutputField from 'automation-ui-action-features/action-outputs/records/OutputField';
import OutputFieldKeys from 'automation-ui-action-features/action-outputs/records/OutputFieldKeys';
import * as ActionMetadataConfigs from 'automation-ui-action-features/actions/ActionMetadataConfigs';
import * as AdsAudienceMembershipActionOperations from 'automation-ui-action-features/actions/ads-audience-membership/AdsAudienceMembershipActionOperations';
import * as AlterListMembershipActionOperations from 'automation-ui-action-features/actions/alter-list-membership/AlterListMembershipActionOperations';
import { fetchFunctionDefinition } from 'automation-ui-action-features/actions/custom-code/CustomCodeApi';
import { FUNCTION_ID } from 'automation-ui-action-features/actions/custom-code/constants/CustomCodeActionMetadataKeys';
import * as OutputFieldUpdateTypes from 'automation-ui-action-features/actions/custom-code/constants/updateTypes';
import { getOutputFieldDefinitionsFromAllActionOutputs, getOutputFieldsFromAllActionOutputs } from 'automation-ui-action-features/actions/custom-code/utils/getOutputFieldsFromAllActionOutputs';
import * as DelayActionSubTypes from 'automation-ui-action-features/actions/delay/constants/DelayActionSubTypes';
import { inferDelayActionSubType } from 'automation-ui-action-features/actions/delay/utils/delayActionUtils';
import * as SetPropertyActionOperations from 'automation-ui-action-features/actions/set-property/SetPropertyActionOperations';
import inferPropertyActionOperationType from 'automation-ui-action-features/actions/set-property/utils/inferPropertyActionOperationType';
import { CREATE } from 'automation-ui-action-features/actions/wait-until/constants/EventFilterKeys';
import { EVENT_FILTERS } from 'automation-ui-action-features/actions/wait-until/constants/WaitUntilActionMetadataKeys';
import * as WebhookActionMetadataKeys from 'automation-ui-action-features/actions/webhook/constants/WebhookActionMetadataKeys';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import ObjectTypeIcons, { DEFAULT_ICON } from 'automation-ui-action-features/constants/ObjectTypeIcons';
import ExtensionActionMetadataDefinition from 'automation-ui-action-features/records/ExtensionActionMetadataDefinition';
import { EXECUTION_STATE_OUTPUT, HUBSPOT_DEFINED_OUTPUT_PREFIX, SERVER_RESPONSE, STATUS_CODE } from 'automation-ui-formula-editor/formula-editor/action-outputs/constants/HubSpotDefinedOutputNames';
import * as SourceApps from 'automation-ui-source-apps/constants/SourceApps';
import { Map as ImmutableMap, List } from 'immutable';
import getIn from 'immutable-less/transmute/getIn';
import { jsx as _jsx } from "react/jsx-runtime";
export const basePathToActionMetadataConfig = (basePath, routeParams) => {
  const TypedActionMetadataConfigs = ActionMetadataConfigs;
  const resultActionMetadataConfigKey = Object.keys(TypedActionMetadataConfigs).find(actionMetadataType => TypedActionMetadataConfigs[actionMetadataType].matchesPath(basePath, routeParams));
  return TypedActionMetadataConfigs[resultActionMetadataConfigKey];
};

/**
 * Prepare a cloned action to be saved:
 * Given an action, clear the appropriate metadata to remove any associations
 * with other objects. This is used when cloning an action.
 * basedOnAction is an optional source of hydrated metadata
 */
export const clearMetadata = (action, basedOnAction) => {
  const actionType = action.getActionType();
  let newAction = action;
  const originalAction = basedOnAction || action;
  switch (actionType) {
    case ActionTypes.AUTOMATION_EXTENSION:
      newAction = newAction.updateMetadata(metadata => metadata.set('extensionInstanceId', null));
      return newAction;
    case ActionTypes.CUSTOM_CODE:
      newAction = newAction.updateMetadata(metadata => metadata.set(FUNCTION_ID, null)).setOutputFields(originalAction.getOutputFields().filter(outputField => {
        return !getIn([OutputFieldKeys.OUTPUT_FIELD_DEFINITION, OutputFieldKeys.TYPE_DEFINITION, OutputFieldKeys.NAME], outputField).startsWith(HUBSPOT_DEFINED_OUTPUT_PREFIX);
      }).map(outputField => outputField.setUpdateType(OutputFieldUpdateTypes.CREATE)).toList());
      return newAction;
    case ActionTypes.FORMULA:
      newAction = newAction.setOutputFields(originalAction.getOutputFields().map(outputField => outputField.setUpdateType(OutputFieldUpdateTypes.CREATE)).toList());
      return newAction;
    case ActionTypes.WAIT_UNTIL:
      {
        const actionMetadata = originalAction.getMetadata();
        const eventFilters = actionMetadata.getEventFilters();
        const newEventFilters = eventFilters.map(filter => filter.setEventFilterId(null).setUpdateType(CREATE));
        newAction = newAction.updateMetadata(metadata => metadata.set(EVENT_FILTERS, newEventFilters));
        return newAction;
      }
    case ActionTypes.WEBHOOK:
      {
        const actionMetadata = originalAction.getMetadata();
        const outputFields = actionMetadata.getOutputFields() || List([]);
        const revisedOutputFields = outputFields.map(output => {
          return output.keySeq().reduce((accumulator, key) => {
            if ([SERVER_RESPONSE, STATUS_CODE, EXECUTION_STATE_OUTPUT].includes(output.get(key).getName())) {
              return accumulator.set(key, output.get(key));
            }
            const revisedOutputName = output.get(key).getLabel() || '';
            const revisedOutput = output.get(key).setName(revisedOutputName);
            return accumulator.set(key, revisedOutput);
          }, ImmutableMap([]));
        });
        newAction = newAction.updateMetadata(metadata => metadata.set(WebhookActionMetadataKeys.ACTION_TYPE_ID, null));
        newAction = newAction.updateMetadata(metadata => metadata.set(WebhookActionMetadataKeys.OUTPUT_FIELDS, List(revisedOutputFields)));
        return newAction;
      }
    default:
      return action;
  }
};
export const hydrateMetadataSynchronously = ({
  action,
  allOutputs,
  functionDefinition
}) => {
  const actionType = action.getActionType();
  switch (actionType) {
    case ActionTypes.CUSTOM_CODE:
      {
        const actionMetadata = action.getMetadata();
        const actionTypeId = actionMetadata.getActionTypeId();
        let hydratedAction = action.set('actionTypeId', actionTypeId);
        if (allOutputs) {
          const outputFields = getOutputFieldsFromAllActionOutputs({
            allOutputsByActionTypeId: allOutputs.actionTypeOutputs,
            actionTypeId
          });
          hydratedAction = hydratedAction.setOutputFields(outputFields);
        }
        if (functionDefinition) {
          hydratedAction = hydratedAction.setMetadata(actionMetadata.set('secrets', functionDefinition.secrets).set('runtime', functionDefinition.runtime));
        }
        return hydratedAction;
      }
    case ActionTypes.WEBHOOK:
      {
        const actionMetadata = action.getMetadata();
        const actionTypeId = actionMetadata.getActionTypeId();
        if (allOutputs) {
          const outputFields = getOutputFieldDefinitionsFromAllActionOutputs({
            allOutputsByActionTypeId: allOutputs.actionTypeOutputs,
            actionTypeId
          });
          return action.updateMetadata(metadata => metadata.set(WebhookActionMetadataKeys.OUTPUT_FIELDS, outputFields));
        }
        return action;
      }
    case ActionTypes.FORMULA:
      {
        if (allOutputs) {
          return action.setOutputFields(allOutputs.instanceOutputs[action.getActionId()].map(output => new OutputField({
            // @ts-expect-error inner value of OutputFieldDefinition_1 is incorrectly typed as unknown
            [OutputFieldKeys.OUTPUT_FIELD_DEFINITION]: output,
            [OutputFieldKeys.UPDATE_TYPE]: 'UPDATE'
          })));
        }
        return action;
      }
    default:
      return action;
  }
};

/**
 * @deprecated Misspelled. Use `hydrateMetadataSynchronously` instead.
 */
export const hydrateMetadataSynchroneously = hydrateMetadataSynchronously;

/*
 * Prepare an action to be cloned, by loading additional metadata that needs to be copied
 */
export const hydrateMetadata = ({
  action
}) => {
  const actionType = action.getActionType();
  switch (actionType) {
    case ActionTypes.CUSTOM_CODE:
      {
        const actionMetadata = action.getMetadata();
        const actionTypeId = actionMetadata.getActionTypeId();
        return Promise.all([fetchFunctionDefinition(actionTypeId), getAllActionOutputs(action.getFlowId())]).then(([definition, allOutputs]) => {
          return hydrateMetadataSynchronously({
            action,
            functionDefinition: definition,
            allOutputs
          });
        });
      }
    case ActionTypes.WEBHOOK:
    case ActionTypes.FORMULA:
      {
        return getAllActionOutputs(action.getFlowId()).then(resp => {
          return hydrateMetadataSynchronously({
            action,
            allOutputs: resp
          });
        });
      }
    default:
      return Promise.resolve(action);
  }
};
export const RECOMMENDED = {
  [SourceApps.DEAL_PIPELINE_SETTINGS]: [ActionMetadataConfigs.TASK, ActionMetadataConfigs.EMAIL_NOTIFICATION],
  [SourceApps.TICKET_PIPELINE_SETTINGS]: [ActionMetadataConfigs.EMAIL, ActionMetadataConfigs.EMAIL_NOTIFICATION],
  [SourceApps.FEEDBACK_FOLLOW_UP]: [ActionMetadataConfigs.EMAIL, ActionMetadataConfigs.EMAIL_NOTIFICATION, ActionMetadataConfigs.TASK, ActionMetadataConfigs.SLACK_NOTIFICATION],
  [SourceApps.ECOMM]: [ActionMetadataConfigs.DELAY_DURATION, ActionMetadataConfigs.EMAIL],
  [SourceApps.LEAD_PIPELINE_SETTINGS]: [ActionMetadataConfigs.TASK, ActionMetadataConfigs.EMAIL]
};
export const getActionMetadataConfigFromActionTypeIdWithoutSubtypes = actionTypeId => {
  var _entriesWithoutSubtyp;
  const excludedSubtypes = ['INCREASE_PROPERTY', 'REMOVE_FROM_LIST'];
  const entriesWithoutSubtypes = Object.entries(ActionMetadataConfigs).filter(([configKey]) => {
    return !excludedSubtypes.includes(configKey);
  });
  return (_entriesWithoutSubtyp = entriesWithoutSubtypes.find(([, value]) => value.get('typeId') === actionTypeId)) === null || _entriesWithoutSubtyp === void 0 ? void 0 : _entriesWithoutSubtyp[1]; // [0] is the key, [1] is the actionMetadataConfig
};
export const getActionMetadataConfigFromActionTypeId = actionTypeId => {
  return Object.values(ActionMetadataConfigs).find(actionMetadataRecord => {
    return actionMetadataRecord.get('typeId') === actionTypeId;
  });
};
/**
 * Given an Action record, return the corresponding FE
 * specific ActionMetadataConfig, also referred to as
 * an `actionDefinition`. The optional configSubType is
 * used in the case that the action metadata is not yet
 * initialized but a subtype distinction has to be made
 * based off this parameter. An example where this is
 * needed is when a New Action is being created.
 */
export const getActionMetadataConfigFromAction = (action, configSubType = '', extensionDefinitions) => {
  const {
    actionType,
    metadata
  } = action;
  switch (actionType) {
    case ActionTypes.AB_TEST_BRANCH:
      return ActionMetadataConfigs.AB_TEST_BRANCH;
    case ActionTypes.CUSTOM_CODE:
      return ActionMetadataConfigs.CUSTOM_CODE;
    case ActionTypes.DELAY:
      {
        const subType = configSubType || inferDelayActionSubType(metadata);
        switch (subType) {
          case DelayActionSubTypes.DURATION:
            return ActionMetadataConfigs.DELAY_DURATION;
          case DelayActionSubTypes.DAY_OR_TIME:
          case DelayActionSubTypes.DAY:
          case DelayActionSubTypes.TIME:
            return ActionMetadataConfigs.DELAY_DAY_OR_TIME;
          default:
            throw new Error(`Unrecognized delay action subType: ${subType}. Please add it to ActionMetadataConfigs#getActionMetadataConfigFromAction.`);
        }
      }
    case ActionTypes.DELAY_UNTIL_DATE:
      return ActionMetadataConfigs.DELAY_UNTIL_DATE;
    case ActionTypes.ALL_DELAYS:
      return ActionMetadataConfigs.ALL_DELAYS;
    case ActionTypes.WAIT_UNTIL:
      return ActionMetadataConfigs.WAIT_UNTIL;
    case ActionTypes.LIST_BRANCH:
      return ActionMetadataConfigs.LIST_BRANCH;
    case ActionTypes.STATIC_BRANCH:
      return ActionMetadataConfigs.STATIC_BRANCH;
    case ActionTypes.ALL_BRANCHES:
      return ActionMetadataConfigs.ALL_BRANCHES;
    case ActionTypes.GO_TO:
      return ActionMetadataConfigs.GO_TO;
    case ActionTypes.TASK:
      return ActionMetadataConfigs.TASK;
    case ActionTypes.EMAIL:
      {
        return ActionMetadataConfigs.EMAIL;
      }
    case ActionTypes.EMAIL_NOTIFICATION:
      return ActionMetadataConfigs.EMAIL_NOTIFICATION;
    case ActionTypes.CREATE_OBJECT:
      {
        const icon = ObjectTypeIcons[action.metadata.objectTypeId] || DEFAULT_ICON;
        return ActionMetadataConfigs.CREATE_OBJECT.set('icon', icon);
      }
    case ActionTypes.SET_PROPERTY:
      {
        const modType = configSubType || inferPropertyActionOperationType(metadata);
        switch (modType) {
          case SetPropertyActionOperations.SET:
            return ActionMetadataConfigs.SET_PROPERTY;
          case SetPropertyActionOperations.INCREASE:
            return ActionMetadataConfigs.INCREASE_PROPERTY;
          default:
            throw new Error(`Unrecognized property action operation type: ${modType}. Please add it to ActionMetadataConfigs#getActionMetadataConfigFromAction.`);
        }
      }
    case ActionTypes.SLACK_NOTIFICATION:
      return ActionMetadataConfigs.SLACK_NOTIFICATION;
    case ActionTypes.IN_APP_NOTIFICATION:
      return ActionMetadataConfigs.IN_APP_NOTIFICATION;
    case ActionTypes.ENROLL_IN_FLOW:
      return ActionMetadataConfigs.ENROLL_IN_FLOW;
    case ActionTypes.ROTATE_OBJECT:
      return ActionMetadataConfigs.ROTATE_OBJECT;
    case ActionTypes.ALTER_LIST_MEMBERSHIP:
      {
        const shouldAdd = configSubType === AlterListMembershipActionOperations.ADD || metadata.shouldAdd;
        return shouldAdd ? ActionMetadataConfigs.ADD_TO_LIST : ActionMetadataConfigs.REMOVE_FROM_LIST;
      }
    case ActionTypes.WEBHOOK:
      return ActionMetadataConfigs.WEBHOOK;
    case ActionTypes.SEND_SMS_NOTIFICATION:
      return ActionMetadataConfigs.SEND_SMS_NOTIFICATION;
    case ActionTypes.CREATE_SALESFORCE_CAMPAIGN:
      return ActionMetadataConfigs.CREATE_SALESFORCE_CAMPAIGN;
    case ActionTypes.CREATE_SALESFORCE_TASK:
      return ActionMetadataConfigs.CREATE_SALESFORCE_TASK;
    case ActionTypes.AUTOMATION_EXTENSION:
      {
        // This record extends ActionMetadata but does not
        // instantiate it with many of the fields as those are
        // taken from the extension definition instead
        const extensionActionMetadataDefinition = new ExtensionActionMetadataDefinition(extensionDefinitions);
        return extensionActionMetadataDefinition;
      }
    case ActionTypes.CLASSIC_COPY_COMPANY_PROPERTY:
      return ActionMetadataConfigs.CLASSIC_COPY_COMPANY_PROPERTY;
    case ActionTypes.CLASSIC_ENROLL_IN_WORKFLOW:
      return ActionMetadataConfigs.CLASSIC_ENROLL_IN_WORKFLOW;
    case ActionTypes.CLASSIC_NOTIFICATION_EMAIL:
      return ActionMetadataConfigs.CLASSIC_NOTIFICATION_EMAIL;
    case ActionTypes.CLASSIC_ANCHORED_DELAY:
      return ActionMetadataConfigs.CLASSIC_ANCHORED_DELAY;
    case ActionTypes.CLASSIC_VIRTUAL_ANCHORED_DELAY:
      return ActionMetadataConfigs.CLASSIC_VIRTUAL_ANCHORED_DELAY;
    case ActionTypes.VIRTUAL_SCHEDULED_ENROLLMENT:
      return ActionMetadataConfigs.VIRTUAL_SCHEDULED_ENROLLMENT;

    /**
     * @deprecated Powered by CLS instead of ILS. Prefer ALTER_LIST_MEMBERSHIP.
     * Keeping around for backwards compatibility.
     */
    case ActionTypes.CLASSIC_ALTER_LIST_MEMBERSHIP:
      {
        const shouldAdd = configSubType === AlterListMembershipActionOperations.ADD || metadata.shouldAdd;
        return shouldAdd ? ActionMetadataConfigs.CLASSIC_ADD_TO_LIST : ActionMetadataConfigs.CLASSIC_REMOVE_FROM_LIST;
      }

    /**
     * @deprecated Keeping for backwards compatibility. Prefer the HubSpot-built
     *   Custom Action instead.
     */
    case ActionTypes.SET_MARKETING_STATUS:
      return ActionMetadataConfigs.SET_MARKETING_STATUS;
    case ActionTypes.UPDATE_EMAIL_SUBSCRIPTION:
      return ActionMetadataConfigs.UPDATE_EMAIL_SUBSCRIPTION;
    case ActionTypes.ADS_AUDIENCE_MEMBERSHIP:
      {
        const shouldAdd = configSubType === AdsAudienceMembershipActionOperations.ADD || metadata.shouldAdd;
        return shouldAdd ? ActionMetadataConfigs.ADS_ADD_TO_AUDIENCE : ActionMetadataConfigs.ADS_REMOVE_FROM_AUDIENCE;
      }
    case ActionTypes.FORMULA:
      return ActionMetadataConfigs.FORMULA;
    case ActionTypes.WHILE_LOOP:
      return ActionMetadataConfigs.WHILE_LOOP;
    default:
      throw new Error(`Unrecognized action type: ${actionType}. Please add it to ActionMetadataConfigs#getActionMetadataConfigFromAction.`);
  }
};
export const ACTION_TYPE_TO_METADATA = {
  [ActionTypes.AB_TEST_BRANCH]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.ab-test-branch.description"
    }),
    configs: [ActionMetadataConfigs.AB_TEST_BRANCH]
  },
  [ActionTypes.ADS_AUDIENCE_MEMBERSHIP]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.ads-audience-membership.description.default"
    }),
    configs: [ActionMetadataConfigs.ADS_ADD_TO_AUDIENCE, ActionMetadataConfigs.ADS_REMOVE_FROM_AUDIENCE]
  },
  [ActionTypes.ALTER_LIST_MEMBERSHIP]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.alter-list-membership.description.default"
    }),
    configs: [ActionMetadataConfigs.ADD_TO_LIST, ActionMetadataConfigs.REMOVE_FROM_LIST]
  },
  [ActionTypes.AUTOMATION_EXTENSION]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.extensions.description"
    }),
    configs: []
  },
  [ActionTypes.CLASSIC_ANCHORED_DELAY]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.classic-anchored-delay.description"
    }),
    configs: [ActionMetadataConfigs.CLASSIC_ANCHORED_DELAY]
  },
  [ActionTypes.CLASSIC_VIRTUAL_ANCHORED_DELAY]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.classic-virtual-anchored-delay.description"
    }),
    configs: [ActionMetadataConfigs.CLASSIC_VIRTUAL_ANCHORED_DELAY]
  },
  [ActionTypes.CLASSIC_COPY_COMPANY_PROPERTY]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.classic-copy-company-property.description"
    }),
    configs: [ActionMetadataConfigs.CLASSIC_COPY_COMPANY_PROPERTY]
  },
  [ActionTypes.CLASSIC_ENROLL_IN_WORKFLOW]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.classic-enroll-in-flow.description"
    }),
    configs: [ActionMetadataConfigs.CLASSIC_ENROLL_IN_WORKFLOW]
  },
  [ActionTypes.CLASSIC_NOTIFICATION_EMAIL]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.classic-notification-email.description"
    }),
    configs: [ActionMetadataConfigs.CLASSIC_NOTIFICATION_EMAIL]
  },
  [ActionTypes.CREATE_OBJECT]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.create-object.description"
    }),
    configs: [ActionMetadataConfigs.CREATE_OBJECT]
  },
  [ActionTypes.CREATE_SALESFORCE_CAMPAIGN]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.create-salesforce-campaign.description"
    }),
    configs: [ActionMetadataConfigs.CREATE_SALESFORCE_CAMPAIGN]
  },
  [ActionTypes.CREATE_SALESFORCE_TASK]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.create-salesforce-task.description"
    }),
    configs: [ActionMetadataConfigs.CREATE_SALESFORCE_TASK]
  },
  [ActionTypes.CUSTOM_CODE]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.custom-code.actionDescription"
    }),
    configs: [ActionMetadataConfigs.CUSTOM_CODE]
  },
  [ActionTypes.DELAY]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.all-delays.description"
    }),
    configs: [ActionMetadataConfigs.DELAY_DURATION, ActionMetadataConfigs.DELAY_DAY_OR_TIME]
  },
  [ActionTypes.DELAY_UNTIL_DATE]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.all-delays.description"
    }),
    configs: [ActionMetadataConfigs.DELAY_UNTIL_DATE]
  },
  [ActionTypes.EMAIL]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.email.description"
    }),
    configs: [ActionMetadataConfigs.EMAIL]
  },
  [ActionTypes.EMAIL_NOTIFICATION]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.email-notification.description"
    }),
    configs: [ActionMetadataConfigs.EMAIL_NOTIFICATION]
  },
  [ActionTypes.ENROLL_IN_FLOW]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.enroll-in-flow.description"
    }),
    configs: [ActionMetadataConfigs.ENROLL_IN_FLOW]
  },
  [ActionTypes.FORMULA]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.formula.description"
    }),
    configs: [ActionMetadataConfigs.FORMULA]
  },
  [ActionTypes.GO_TO]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.go-to.description"
    }),
    configs: [ActionMetadataConfigs.GO_TO]
  },
  [ActionTypes.IN_APP_NOTIFICATION]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.in-app-notification.description"
    }),
    configs: [ActionMetadataConfigs.IN_APP_NOTIFICATION]
  },
  [ActionTypes.LIST_BRANCH]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.all-branches.description"
    }),
    configs: [ActionMetadataConfigs.LIST_BRANCH]
  },
  [ActionTypes.ROTATE_OBJECT]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.rotate-object.description"
    }),
    configs: [ActionMetadataConfigs.ROTATE_OBJECT]
  },
  [ActionTypes.SEND_SMS_NOTIFICATION]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.send-internal-sms.description"
    }),
    configs: [ActionMetadataConfigs.SEND_SMS_NOTIFICATION]
  },
  [ActionTypes.SET_PROPERTY]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.set-property.description"
    }),
    configs: [ActionMetadataConfigs.INCREASE_PROPERTY, ActionMetadataConfigs.SET_PROPERTY]
  },
  [ActionTypes.SLACK_NOTIFICATION]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.slack.description"
    }),
    configs: [ActionMetadataConfigs.SLACK_NOTIFICATION]
  },
  [ActionTypes.ALL_BRANCHES]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.all-branches.description"
    }),
    configs: [ActionMetadataConfigs.ALL_BRANCHES]
  },
  [ActionTypes.ALL_DELAYS]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.all-delays.description"
    }),
    configs: [ActionMetadataConfigs.ALL_DELAYS]
  },
  [ActionTypes.STATIC_BRANCH]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.all-branches.description"
    }),
    configs: [ActionMetadataConfigs.STATIC_BRANCH]
  },
  [ActionTypes.TASK]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.create-task.description"
    }),
    configs: [ActionMetadataConfigs.TASK]
  },
  [ActionTypes.VIRTUAL_SCHEDULED_ENROLLMENT]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.virtual-scheduled-enrollment.description"
    }),
    configs: [ActionMetadataConfigs.VIRTUAL_SCHEDULED_ENROLLMENT]
  },
  [ActionTypes.WAIT_UNTIL]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.all-delays.description"
    }),
    configs: [ActionMetadataConfigs.WAIT_UNTIL]
  },
  [ActionTypes.WEBHOOK]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.webhook.description"
    }),
    configs: [ActionMetadataConfigs.WEBHOOK]
  },
  [ActionTypes.WHILE_LOOP]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.while-loop.description"
    }),
    configs: [ActionMetadataConfigs.WHILE_LOOP]
  },
  /**
   * Temporarily reintroducing SET_MARKETING_STATUS and UPDATE_EMAIL_SUBSCRIPTION
   * to use their generalDescription for action type filter
   * Context: https://git.hubteam.com/HubSpot/automation-ui-action-features/pull/3041
   */
  [ActionTypes.UPDATE_EMAIL_SUBSCRIPTION]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.set-subscriptions.description"
    }),
    configs: [ActionMetadataConfigs.UPDATE_EMAIL_SUBSCRIPTION]
  },
  [ActionTypes.SET_MARKETING_STATUS]: {
    generalDescription: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-action-features.action-features.set-marketing-status.description"
    }),
    configs: [ActionMetadataConfigs.SET_MARKETING_STATUS]
  }
};